import { ColumnDefinition, Flex, Link } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { DoneIcon } from 'modules/sprites';
import moment from 'moment';
import { getPath, Path } from 'urlBuilder';

import { messages } from './Content.messages';
import { GetColumnDefinitionsParams, NetSuiteMatchingLine } from './Content.types';

export const getColumnDefinitions = ({ request, lineType }: GetColumnDefinitionsParams) => {
    const columnDefinitions: ColumnDefinition<NetSuiteMatchingLine>[] = [];

    if (request.integrationCode === domain.IntegrationCode.NetSuitePO) {
        columnDefinitions.push(
            {
                id: 'billFriendlyName',
                name: messages.billColumnName,
                value: (li) => (
                    <Link
                        font='body'
                        title={`${li.name}\n${messages.totalAmount({ totalAmount: li.totalAmount })}`}
                        href={getPath(Path.request, li.requestId, request.companyId)}
                        external
                    >
                        {li.name}
                    </Link>
                ),
                minWidth: 150,
            },
            {
                id: 'billDate',
                name: messages.dateColumnName,
                value: (li) => {
                    const date = moment.utc(li.date).format('ll');

                    return (
                        <Link
                            font='body'
                            title={`${date}\n${messages.totalAmount({ totalAmount: li.totalAmount })}`}
                            href={getPath(Path.request, li.requestId, request.companyId)}
                            external
                        >
                            {date}
                        </Link>
                    );
                },
                minWidth: 100,
            }
        );
    }

    if (lineType === 'Expense-lines') {
        columnDefinitions.push(
            {
                id: 'category',
                name: messages.categoryColumnName,
                value: (li) => li.category?.text ?? null,
            },
            {
                id: 'account',
                name: messages.accountColumnName,
                value: (li) => li.account?.text ?? null,
            }
        );
    }

    if (lineType === 'Item-lines') {
        columnDefinitions.push(
            {
                id: 'item',
                name: messages.itemColumnName,
                value: (li) => li.item?.text ?? null,
            },
            {
                id: 'description',
                name: messages.descriptionColumnName,
                value: (li) => li.description ?? null,
            },
            {
                id: 'quantity',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.quantityColumnName,
                value: (li) => intl.formatNumber(li.quantity, 'auto') ?? null,
            },
            {
                id: 'unitPrice',
                textAlign: 'right',
                headerTextAlign: 'right',
                name: messages.unitPriceColumnName,
                value: (li) => intl.formatNumber(li.unitPrice, 'auto') ?? null,
            }
        );
    }

    columnDefinitions.push(
        {
            id: 'amount',
            name: messages.amountColumnName,
            value: (li) => intl.formatNumber(li.amount, 'auto') ?? null,
        },
        {
            id: 'taxCode',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.taxCodeColumnName,
            value: (li) => li.taxCode?.text ?? null,
        },
        {
            id: 'taxRate',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.taxRateColumnName,
            value: (li) => intl.formatNumber(li.taxRate, 'auto') ?? null,
        },
        {
            id: 'taxAmount',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.taxAmountColumnName,
            value: (li) => intl.formatNumber(li.taxAmount, 'auto') ?? null,
        },
        {
            id: 'grossAmount',
            textAlign: 'right',
            headerTextAlign: 'right',
            name: messages.grossAmountColumnName,
            value: (li) => intl.formatNumber(li.grossAmount, 'auto') ?? null,
        },
        {
            id: 'memo',
            name: messages.memoColumnName,
            value: (li) => li.memo ?? null,
        },
        {
            id: 'department',
            name: messages.departmentColumnName,
            value: (li) => li.department?.text ?? null,
        },
        {
            id: 'class',
            name: messages.classColumnName,
            value: (li) => li.class?.text ?? null,
        },
        {
            id: 'location',
            name: messages.locationColumnName,
            value: (li) => li.location?.text ?? null,
        },
        {
            id: 'customer',
            name: messages.customerColumnName,
            value: (li) => li.customer?.text ?? null,
        },
        {
            id: 'isBillable',
            name: messages.isBillableColumnName,
            value: (li) =>
                li.isBillable ? (
                    <Flex alignItems='center'>
                        <DoneIcon width={13} height={10} />
                    </Flex>
                ) : null,
        }
    );

    return columnDefinitions.filter(({ value }) => value !== null);
};
